// src/components/ManageAdministrators.js

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchUsers,
  addUser,
  updateUser,
  deleteUser,
} from "../slices/accountSlice";
import {
  Alert,
  Button,
  Form,
  Input,
  Layout,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { Header, Content } = Layout;
const { Title } = Typography;
const ManageAdministrators = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.account.user);
  const currentUsername = user.username;
  const isAdmin = user.is_superuser;
  const { staff = [], loading, error } = useSelector(
    (state) => state.account || {}
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchUsers()).catch(() =>
      message.error("Failed to load members")
    );
  }, [dispatch]);

  const handleOpenModal = (member = null) => {
    setEditingMember(member);
    form.setFieldsValue({
      first_name: member?.first_name || "",
      last_name: member?.last_name || "",
      email: member?.email || "",
      role: member?.is_superuser ? "admin" : staff?.is_staff ? "staff" : "staff",
    });
    setIsModalVisible(true);
  };


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const updatedValues = {
        ...values,
        is_superuser: values.role === "admin",
        is_staff: values.role === "admin" || values.role === "staff",
      };

      delete updatedValues.role;

      if (editingMember) {
        dispatch(updateUser({ id: editingMember.username, ...updatedValues }));
      } else {
        dispatch(addUser(updatedValues));
      }

      setIsModalVisible(false);
      dispatch(fetchUsers());
    } catch (errorInfo) {
      console.log("Validation failed:", errorInfo);
    }
  };

  const handleDelete = async (id) => {
    dispatch(deleteUser(id));
    message.success("Staff deleted.");
    dispatch(fetchUsers());
  };

  const columns = [
    {
      title: "Given Name",
      key: "first_name",
      render: (text, record) => `${record.first_name}`,
      sorter: (a, b) => a.first_name.length - b.first_name.length,
    },
    {
      title: "Family Name",
      key: "last_name",
      render: (text, record) => `${record.last_name}`,
      sorter: (a, b) => a.last_name.length - b.last_name.length,
    },
    { title: "Email", dataIndex: "email" },
    {
      title: "Role",
      render: (_, record) => (record.is_superuser ? "Admin" : record.is_staff ? "Staff" : "User"),
      dataIndex: "role",
      filters:[{text: "Admin", value: "is_superuser"},{text:"Staff", value: "is_staff"}],
      onFilter: (value, record) => {
        if (value === "is_superuser") {
          return record.is_superuser === true
        }
        if (value === "is_staff") {
          return record.is_superuser === false
        }
      },
    },
    {
      title: "Date Joined",
      dataIndex: "date_joined",
      render: (date) => date ? dayjs(date).format("MMM D, YYYY h:mm A") : "N/A",
      sorter: (a, b) => dayjs(a.date_joined) - dayjs(b.date_joined)
    },
    {
      title: "Actions",
      render: (_, record) => {
        const isSelf = record?.username && record.username === currentUsername;
        return (
          <>
            <Tooltip title="Edit administrator">
              <Button
                icon={<EditOutlined className="action-btn"/>}
                onClick={() => handleOpenModal(record)}
                style={{ marginRight: 8 }}
                disabled={!isAdmin}
              />
            </Tooltip>
            <Tooltip title={isSelf ? "You cannot delete yourself" : "Delete Administrator"}>
              <Popconfirm
                title="Are you sure you want to delete this user?"
                onConfirm={() => handleDelete(record?.username)}
                okText="Yes"
                cancelText="No"
                disabled={isSelf}
              >
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  disabled={isSelf || !isAdmin}
                />
              </Popconfirm>
            </Tooltip>
          </>
        );
      },
    }
  ];

  return (
    <Layout className="admin-layout">
      <Header className="primary-header">
        <div>
          <Tooltip title="Add new staff or admin">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              className="header-button"
              disabled={!isAdmin}
            >Add new user</Button>
          </Tooltip>
        </div>
        <Title className="primary-title">Manage Administrators</Title>
      </Header>

        {loading ? (
          <Spin />
        ) : error ? (
          <Alert
            message="Error fetching staff"
            description={error}
            type="error"
            showIcon
          />
        ) : ( null)}
          <Table
            className="table"
            columns={columns}
            dataSource={staff.filter(Boolean)}
            rowKey="username"
            bordered
          />
        <Modal
          className="uci-modal"
          title={editingMember ? "Edit Member" : "Add New Member"}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onOk={handleSubmit}
        >
          <Form form={form} layout="horizontal">
            <Form.Item
              name="first_name"
              label="Given Name"
              rules={[{ required: true, message: "Enter given(first) name" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="last_name"
              label="Family Name"
              rules={[{ required: true, message: "Enter family(last) name" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: "Enter email" }]}
            >
              <Input type="email" />
            </Form.Item>
            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: "Select a role" }]}
            >
              <Select placeholder="Select role">
                <Option value="admin">Admin</Option>
                <Option value="staff">Staff</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
  );
};

export default ManageAdministrators;
